// Centralized API endpoints map
// Use these constants to avoid hardcoding paths in multiple places

export const ENDPOINTS = {
  audits: {
    list: () => `/audit`,
    one: (auditId: string | number) => `/audit/${auditId}`,
    create: () => `/audit/create`,
    update: (auditId: string | number) => `/audit/${auditId}/update`,
    delete: (auditId: string | number) => `/audit/${auditId}`,
    archive: (auditId: string | number) => `/audit/${auditId}/archive`,
    archiveStatus: (auditId: string | number) => `/audit/${auditId}/archive/status`,
    status: (auditId: string | number) => `/audit/${auditId}/status`,
    roles: (auditId: string | number) => `/audit/${auditId}/roles`,
    rolesItem: (auditId: string | number, roleId: string | number) =>
      `/audit/${auditId}/roles/${roleId}`,
    rolesCreate: (auditId: string | number) => `/audit/${auditId}/roles/create`,
    rolesUpdate: (auditId: string | number, roleId: string | number) =>
      `/audit/${auditId}/roles/${roleId}/update`,
    cancelArchivation: (auditId: string | number) => `/audit/${auditId}/cancel-archivation`,
    generateReport: (auditId: string | number) => `/audit/${auditId}/generate-report`,
    generateReportStatus: (auditId: string | number) => `/audit/${auditId}/generate-report/status`,
    involvedCompanyMembers: (auditId: string | number) =>
      `/audit/${auditId}/involved-company-members`,
    myTeam: (auditId: string | number) => `/audit/${auditId}/my-team`,
    auditorFirmMembers: (auditId: string | number) => `/audit/${auditId}/auditor-firm-members`,
    addAuditorFirmMember: (auditId: string | number) => `/audit/${auditId}/add-auditor`,
    removeAuditorFirmMember: (auditId: string | number, memberId: string | number) =>
      `/audit/${auditId}/auditor/${memberId}`,
    changeAuditorFirmMemberRole: (auditId: string | number, memberId: string | number) =>
      `/audit/${auditId}/auditor/${memberId}/role`,
    confirmIndependence: (auditId: string | number) => `/audit/${auditId}/independence/confirm`,
    rejectIndependence: (auditId: string | number) => `/audit/${auditId}/independence/reject`,
    teamMembers: (auditId: string | number) => `/audit/${auditId}/team-members`,
    participants: (auditId: string | number) => `/audit/${auditId}/participants`,
    teamMembersItem: (auditId: string | number, memberId: string | number) =>
      `/audit/${auditId}/team-members/${memberId}`,
    teamMe: (auditId: string | number) => `/audit/${auditId}/team-members/me`,
    inviteTeamMember: (auditId: string | number) => `/audit/${auditId}/team-members/invite`,
    teamMembersUpdate: (auditId: string | number, memberId: string | number) =>
      `/audit/${auditId}/team-members/${memberId}/update`,
  },
  assertions: {
    list: (auditId: string | number) => `/audit/${auditId}/assertions`,
    create: (auditId: string | number) => `/audit/${auditId}/assertions`,
  },
  company: {
    company: '/company',
    create: '/company/create',
    update: (id: string | number) => `/company/${id}/update`,
    commercialRegister: (companyId: string | number) => `/company/${companyId}/commercial-register`,
    companyItem: (companyId: string | number) => `/company/${companyId}`,
    teamMembers: (id: string | number) => `/company/${id}/team-members`,
    memberChangeRole: (companyId: string | number, memberId: string | number) =>
      `/company/${companyId}/member/${memberId}/change-role`,
    memberSignatory: (companyId: string | number, memberId: string | number) =>
      `/company/${companyId}/member/${memberId}/signatory`,
    memberDelete: (id: string | number, memberId: string | number) =>
      `/company/${id}/member/${memberId}`,
    zefix: () => '/company/zefix',
    legalForms: '/company/legal-forms',
    legalForm: (id: string | number) => `/company/${id}/legal-form`,
  },
  documents: {
    base: (auditId: string) => `/audit/${auditId}/documents`,
    allDeleted: (auditId: string) => `/audit/${auditId}/documents/all/deleted`,
    withBacklinks: (auditId: string) => `/audit/${auditId}/documents/with-backlinks`,
    one: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}`,
    editor: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}/editor`,
    backlinks: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/backlinks`,
    editorAnnotations: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/editor/annotations`,
    editorAnnotationItem: (auditId: string, id: string | number, annotationId: string | number) =>
      `/audit/${auditId}/documents/${id}/editor/annotations/${annotationId}`,
    editorAnnotationsBulk: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/editor/annotations/bulk`,
    editorHistory: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/editor/history`,
    editorWorkPerformed: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/editor/work-performed`,
    editorMetadata: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/editor/metadata`,
    editorGroups: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/editor/groups`,
    sync: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}/sync`,
    verifySwisscom: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/verify-swisscom`,
    sign: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}/sign`,
    update: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}/update`,
    replace: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}/replace`,
    delete: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}`,
    restore: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}/restore`,
    comment: (auditId: string, id: string | number) => `/audit/${auditId}/documents/${id}/comment`,
    download: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/download`,
    inviteGuest: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/invite/guest`,
    create: (auditId: string) => `/audit/${auditId}/documents`,
    createMultiple: (auditId: string) => `/audit/${auditId}/documents/multiple`,
    updateSignatures: (auditId: string, id: string | number) =>
      `/audit/${auditId}/documents/${id}/signers/send`,
  },
  documentTypes: {
    list: (auditId: string) => `/audit/${auditId}/document-types`,
    create: (auditId: string) => `/audit/${auditId}/document-types`,
    update: (auditId: string, documentTypeId: string | number) =>
      `/audit/${auditId}/document-types/${documentTypeId}`,
    delete: (auditId: string, documentTypeId: string | number) =>
      `/audit/${auditId}/document-types/${documentTypeId}`,
  },
  bexio: {
    login: () => `/bexio/login`,
    token: () => `/bexio/token`,
    disconnect: () => `/bexio/disconnect`,
    export: (auditId: string, documentId: string | number) =>
      `/bexio/${auditId}/export/${documentId}`,
    exportStatus: (auditId: string, documentId: string | number) =>
      `/bexio/${auditId}/export/${documentId}/status`,
    refetch: (auditId: string, documentId: string | number) =>
      `/bexio/${auditId}/refetch/${documentId}`,
    refetchStatus: (auditId: string, documentId: string | number) =>
      `/bexio/${auditId}/refetch/${documentId}/status`,
  },
  meetings: {
    list: (auditId: string) => `/audit/${auditId}/meetings`,
    all: (auditId: string) => `/audit/${auditId}/meetings/all`,
    one: (auditId: string, meetingId: string | number) => `/audit/${auditId}/meetings/${meetingId}`,
    create: (auditId: string) => `/audit/${auditId}/meetings`,
    update: (auditId: string, meetingId: string | number) =>
      `/audit/${auditId}/meetings/${meetingId}/update`,
    delete: (auditId: string, meetingId: string | number) =>
      `/audit/${auditId}/meetings/${meetingId}`,
    /** The places that cite this meeting (0.54.0). Client-reachable; auditor-only
     *  sources are filtered for non-auditors, so the count can be short of the guard's. */
    backlinks: (auditId: string, meetingId: string | number) =>
      `/audit/${auditId}/meetings/${meetingId}/backlinks`,
    attachDocument: (auditId: string, meetingId: string | number) =>
      `/audit/${auditId}/meetings/${meetingId}/attach-document`,
    detachDocument: (auditId: string, meetingId: string | number) =>
      `/audit/${auditId}/meetings/${meetingId}/detach-document`,
    comment: (auditId: string, meetingId: string | number) =>
      `/audit/${auditId}/meetings/${meetingId}/comment`,
  },
  requests: {
    list: (auditId: string) => `/audit/${auditId}/requests`,
    one: (auditId: string, requestId: string | number) => `/audit/${auditId}/requests/${requestId}`,
    create: (auditId: string) => `/audit/${auditId}/requests`,
    reorder: (auditId: string) => `/audit/${auditId}/requests/reorder`,
    duplicate: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/duplicate`,
    patch: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}`,
    assignees: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/assignees`,
    pin: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/pin`,
    subscription: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/subscription`,
    activity: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/activity`,
    backlinks: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/backlinks`,
    exampleDocument: (
      auditId: string,
      requestId: string | number,
      exampleDocumentId: string | number,
    ) => `/audit/${auditId}/requests/${requestId}/example-documents/${exampleDocumentId}`,
    delete: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}`,
    send: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/send`,
    approve: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/approve`,
    reject: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/reject`,
    attachDocument: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/attach-document`,
    detachDocument: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/detach-document`,
    comment: (auditId: string, requestId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/comment`,
    deleteComment: (auditId: string, requestId: string | number, commentId: string | number) =>
      `/audit/${auditId}/requests/${requestId}/comment/${commentId}`,
    blocks: {
      create: (auditId: string, requestId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks`,
      fromTemplate: (auditId: string, requestId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/from-template`,
      reorder: (auditId: string, requestId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/reorder`,
      one: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}`,
      delete: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}`,
      status: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/status`,
      activity: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/activity`,
      comments: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/comments`,
      comment: (
        auditId: string,
        requestId: string | number,
        blockId: string | number,
        commentId: string | number,
      ) => `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/comments/${commentId}`,
      response: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/response`,
      responseDocuments: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/response/documents`,
      responseDocument: (
        auditId: string,
        requestId: string | number,
        blockId: string | number,
        documentId: string | number,
      ) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/response/documents/${documentId}`,
      responseHistory: (auditId: string, requestId: string | number, blockId: string | number) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/response/history`,
      aiFeedbackAuditorRatingTags: (
        auditId: string,
        requestId: string | number,
        blockId: string | number,
        feedbackId: string | number,
      ) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/ai-feedback/${feedbackId}/auditor-rating-tags`,
      aiFeedbackClientFeedback: (
        auditId: string,
        requestId: string | number,
        blockId: string | number,
        feedbackId: string | number,
      ) =>
        `/audit/${auditId}/requests/${requestId}/blocks/${blockId}/ai-feedback/${feedbackId}/client-feedback`,
    },
    quote: {
      update: (auditId: string | number) => `/audit/${auditId}/requests/quote/update`,
    },
  },
  requestTemplates: {
    list: () => `/team/request-templates`,
    instantiable: () => `/team/request-templates/instantiable`,
    create: () => `/team/request-templates`,
    one: (templateId: string | number) => `/team/request-templates/${templateId}`,
    delete: (templateId: string | number) => `/team/request-templates/${templateId}`,
    draft: (templateId: string | number) => `/team/request-templates/${templateId}/draft`,
    version: (templateId: string | number, versionId: string | number) =>
      `/team/request-templates/${templateId}/versions/${versionId}`,
    versionActivity: (templateId: string | number, versionId: string | number) =>
      `/team/request-templates/${templateId}/versions/${versionId}/activity`,
    upgradeVersionBlock: (
      templateId: string | number,
      versionId: string | number,
      blockTemplateId: string | number,
    ) =>
      `/team/request-templates/${templateId}/versions/${versionId}/blocks/${blockTemplateId}/upgrade`,
    downgradeVersionBlock: (
      templateId: string | number,
      versionId: string | number,
      blockTemplateId: string | number,
    ) =>
      `/team/request-templates/${templateId}/versions/${versionId}/blocks/${blockTemplateId}/downgrade`,
    submit: (templateId: string | number, versionId: string | number) =>
      `/team/request-templates/${templateId}/versions/${versionId}/submit`,
    review: (templateId: string | number, versionId: string | number) =>
      `/team/request-templates/${templateId}/versions/${versionId}/review`,
    duplicate: (templateId: string | number) => `/team/request-templates/${templateId}/duplicate`,
    shares: (templateId: string | number) => `/team/request-templates/${templateId}/shares`,
    ownershipTransfer: (templateId: string | number) =>
      `/team/request-templates/${templateId}/ownership-transfer`,
    instantiate: (templateId: string | number) =>
      `/team/request-templates/${templateId}/instantiate`,
    fromRequest: () => `/team/request-templates/from-request`,
    fromRequestDraft: () => `/team/request-templates/from-request/draft`,
  },
  blockTemplates: {
    list: () => `/team/request-block-templates`,
    create: () => `/team/request-block-templates`,
    fromBlock: () => `/team/request-block-templates/from-block`,
    fromBlockDraft: () => `/team/request-block-templates/from-block/draft`,
    one: (templateId: string | number) => `/team/request-block-templates/${templateId}`,
    delete: (templateId: string | number) => `/team/request-block-templates/${templateId}`,
    draft: (templateId: string | number) => `/team/request-block-templates/${templateId}/draft`,
    version: (templateId: string | number, versionId: string | number) =>
      `/team/request-block-templates/${templateId}/versions/${versionId}`,
    versionActivity: (templateId: string | number, versionId: string | number) =>
      `/team/request-block-templates/${templateId}/versions/${versionId}/activity`,
    submit: (templateId: string | number, versionId: string | number) =>
      `/team/request-block-templates/${templateId}/versions/${versionId}/submit`,
    review: (templateId: string | number, versionId: string | number) =>
      `/team/request-block-templates/${templateId}/versions/${versionId}/review`,
    duplicate: (templateId: string | number) =>
      `/team/request-block-templates/${templateId}/duplicate`,
    shares: (templateId: string | number) => `/team/request-block-templates/${templateId}/shares`,
    ownershipTransfer: (templateId: string | number) =>
      `/team/request-block-templates/${templateId}/ownership-transfer`,
  },
  exampleDocuments: {
    list: () => `/team/request-example-documents`,
    upload: () => `/team/request-example-documents`,
    fromAuditDocument: (auditId: string | number, documentId: string | number) =>
      `/audit/${auditId}/documents/${documentId}/example-document`,
    getOne: (id: string | number) => `/team/request-example-documents/${id}`,
    rename: (id: string | number) => `/team/request-example-documents/${id}`,
  },
  presets: {
    list: () => '/presets',
  },
  superadmin: {
    selectAuditorFirm: () => '/superadmin/select_audit_firm',
    teams: () => '/superadmin/teams',
    users: () => '/superadmin/users',
    user: (userId: string | number) => `/superadmin/users/${userId}`,
    userCopyInvite: (userId: string | number) => `/superadmin/users/${userId}/copy-invite`,
    userToggleSuperadmin: (userId: string | number) =>
      `/superadmin/users/${userId}/toggle-superadmin`,
    userUpdate: (userId: string | number) => `/superadmin/users/${userId}/update`,
    userDelete: (email: string) => `/superadmin/users/${email}`,
    companies: () => '/superadmin/companies',
    company: '/superadmin/company',
    companyItem: (type: string, id: string | number) => `/superadmin/company/${type}/${id}`,
    companyUpdate: (type: string, id: string | number) =>
      `/superadmin/company/${type}/${id}/update`,
    companyInvite: (type: string, id: string | number) =>
      `/superadmin/company/${type}/${id}/invite`,
    companyMember: (type: string, id: string | number, memberId: string | number) =>
      `/superadmin/company/${type}/${id}/member/${memberId}`,
    companyMemberChangeRole: (type: string, id: string | number, memberId: string | number) =>
      `/superadmin/company/${type}/${id}/member/${memberId}/change-role`,
    companyMemberSignatory: (type: string, id: string | number, memberId: string | number) =>
      `/superadmin/company/${type}/${id}/member/${memberId}/signatory`,
    translationsExport: () => '/superadmin/translations/export',
    vouchers: () => '/superadmin/vouchers',
    voucher: (voucherId: string | number) => `/superadmin/vouchers/${voucherId}`,
    toggleVoucherUsage: (voucherId: string | number) => `/superadmin/vouchers/${voucherId}/toggle`,
    detachVoucher: (voucherId: string | number) => `/superadmin/vouchers/${voucherId}/detach`,
    aiConfig: () => '/superadmin/ai-config',
    aiScenario: (scenarioKey: string) =>
      `/superadmin/ai-config/scenarios/${encodeURIComponent(scenarioKey)}`,
    aiScenarioExport: (scenarioKey: string) =>
      `/superadmin/ai-config/scenarios/${encodeURIComponent(scenarioKey)}/export`,
    aiTemplate: (templateKey: string) =>
      `/superadmin/ai-config/templates/${encodeURIComponent(templateKey)}`,
    aiTemplateReset: (templateKey: string) =>
      `/superadmin/ai-config/templates/${encodeURIComponent(templateKey)}/reset`,
    aiTemplatePreview: (templateKey: string, scenarioKey?: string | null) =>
      `/superadmin/ai-config/templates/${encodeURIComponent(templateKey)}/preview${
        scenarioKey ? `?scenarioKey=${encodeURIComponent(scenarioKey)}` : ''
      }`,
    importRuns: () => '/superadmin/import-runs',
    importRunsSummary: () => '/superadmin/import-runs/summary',
    importRunsValidationSummary: () => '/superadmin/import-runs/validation-summary',
    importRun: (jobId: string | number) => `/superadmin/import-runs/${jobId}`,
  },
  user: {
    user: () => '/user',
    login: () => '/user/login',
    logout: () => '/user/logout',
    verifyIdentity: () => '/user/verify-identity',
    forgotPasswordEmail: () => '/user/forgot-password-email',
    verifySms: () => '/user/verify-sms',
    sendSms: () => '/user/send-sms',
    getAllUsers: () => '/user/get-all-users',
    resendCode: () => '/user/resend-code',
    phone: () => '/user/phone',
    setPassword: () => '/user/set-password',
    resetSendSms: () => '/user/reset-send-sms',
    resetVerifySms: () => '/user/reset-verify-sms',
    resetPassword: () => '/user/reset-password',
    changePassword: () => '/user/forgot-password',
    isActive: () => '/user/is-active',
    notifications: (type: string) => `/user/notifications?type=${type}`,
    readGeneralNotifications: () => '/user/read-general-notifications',
    readImportantNotifications: () => '/user/read-important-notifications',
    signature: () => '/user/signature',
  },
  team: {
    team: () => '/team',
    auditorFirms: () => '/team/auditor-firms',
    update: () => '/team/update',
    invite: () => '/team/invite',
    member: (id: string | number) => `/team/member/${id}`,
    signatory: (id: string | number) => `/team/member/${id}/signatory`,
  },
  dataAnalysis: {
    // CAP-025 — view governance.
    views: (auditId: string) => `/audit/${auditId}/data-analysis/views`,
    viewsDeleted: (auditId: string) => `/audit/${auditId}/data-analysis/views/deleted`,
    viewsReorder: (auditId: string) => `/audit/${auditId}/data-analysis/views/reorder`,
    view: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}`,
    viewDuplicate: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/duplicate`,
    viewRestore: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/restore`,
    viewActivity: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/activity`,
    viewBacklinks: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/backlinks`,

    // CAP-025 — the read layer. All GETs, so they work on a frozen audit; the
    // config-in-body POST reads were retired in contract v1.6.
    /** Renders a saved view, dispatching on its layout. Takes `overrides`. */
    viewQuery: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/query`,
    viewHistogram: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/histogram`,
    /**
     * The categorical filter picker's OPTION SET for one dimension of one view
     * (handoff 2026-09-10 §10c) — browse and search, answered over the view's whole
     * composition rather than over the pages a client happens to have scrolled.
     *
     * The server lifts every categorical card on the requested dimension first, so the
     * picker can broaden past a card the auditor is editing. Values are stable CODES:
     * the label join is the client's (registry dims via `/mapping/registry`).
     */
    viewDimensionValues: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/dimension-values`,
    /** The Selection panel's server-side totals. Never call this "tie-out". */
    selectionSummary: (auditId: string) => `/audit/${auditId}/data-analysis/selection-summary`,

    // CAP-027 account mapping. The registry is the code→label seam for every pivot value:
    // reads project stable codes, the client resolves display names here.
    mappingRegistry: (auditId: string) => `/audit/${auditId}/data-analysis/mapping/registry`,
    /** GET lists every assignment; PUT is the single write for every mapping gesture. */
    mapping: (auditId: string) => `/audit/${auditId}/data-analysis/mapping`,
    /**
     * v1.11 node edits, both engagement-local (ADR-022 — the platform catalogue and every
     * other audit are untouched). Non-GET deliberately: every non-GET 404s on a frozen or
     * archived audit, and that is the whole freeze guard.
     */
    /** PATCH `{ code, label }` — renames one report LINE. The code is identity, never written. */
    mappingRegistryReportLine: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/report-line`,
    /**
     * PATCH `{ level, code, label }` — the level-generic rename (report type, section, line
     * or audit area). The line path above is the retained alias of this one; the client
     * calls this for `audit_area`, the only level the alias cannot name.
     */
    mappingRegistryNode: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/node`,
    /** POST `{ sectionCode, orderedCodes }` — declares a section's whole membership and order. */
    mappingRegistryReportLinesReorder: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/report-lines/reorder`,
    /** POST `{ reportTypeCode, orderedCodes }` — the section mirror: a type's sections. */
    mappingRegistrySectionsReorder: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/sections/reorder`,
    /** POST `{ orderedCodes }` — every report type, in statement order. */
    mappingRegistryTypesReorder: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/types/reorder`,
    /**
     * POST `{ level, code, confirmation? }` — deletes one hierarchy node and its subtree.
     *
     * TWO-PHASE: the first call omits `confirmation` and is REFUSED with a 409
     * `registry_delete_confirmation` carrying the identity digests to echo back. That is
     * the contract, not a retry-on-failure — the digests pin the exact subtree and account
     * set the auditor was shown, so a concurrent edit re-asks instead of deleting
     * something else. The node's identity rides the BODY, never the path: a code is opaque
     * catalogue data and must not need URL encoding.
     */
    mappingRegistryNodeDelete: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/nodes/delete`,
    /** POST `{ changeId }` — restores a deleted subtree AND the mappings it took with it. */
    mappingRegistryDeletionUndo: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/deletions/undo`,
    /** POST `{ level, parentCode, label, requestId }` — one empty line or section (#640). */
    mappingRegistryNodeCreate: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/nodes/create`,
    /** POST `{ level, code, label?, requestId }` — the node alone, at the end of its parent. */
    mappingRegistryNodeDuplicate: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/nodes/duplicate`,
    /** POST `{ changeId }` — takes a created node away again, while nothing refers to it. */
    mappingRegistryCreationUndo: (auditId: string) =>
      `/audit/${auditId}/data-analysis/mapping/registry/creations/undo`,

    // CAP-025 v1.6 — the annotation plane.
    symbols: (auditId: string) => `/audit/${auditId}/data-analysis/symbols`,
    symbol: (auditId: string, symbolId: string | number) =>
      `/audit/${auditId}/data-analysis/symbols/${symbolId}`,
    /** GET `?excludeViewId=` — the symbol's instances outside the current view (DOC-15). */
    symbolViews: (auditId: string, symbolId: string | number) =>
      `/audit/${auditId}/data-analysis/symbols/${symbolId}/views`,
    symbolReassign: (auditId: string, symbolId: string | number) =>
      `/audit/${auditId}/data-analysis/symbols/${symbolId}/reassign`,
    placements: (auditId: string, dataSourceId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSourceId}/placements`,
    /** Place or clear ONE symbol across a whole scope — the set form of `placements`. */
    placementsBulk: (auditId: string, dataSourceId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSourceId}/placements/bulk`,
    placement: (auditId: string, placementId: string | number) =>
      `/audit/${auditId}/data-analysis/placements/${placementId}`,
    placementRetype: (auditId: string, placementId: string | number) =>
      `/audit/${auditId}/data-analysis/placements/${placementId}/retype`,
    viewAnnotations: (auditId: string, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/views/${viewId}/annotations`,

    // CAP-025 v1.6 — journal-entry tags.
    tags: (auditId: string) => `/audit/${auditId}/data-analysis/tags`,
    /** PATCH `{ label?, color? }` renames one tag definition; DELETE soft-deletes it. */
    tag: (auditId: string, tagId: string | number) =>
      `/audit/${auditId}/data-analysis/tags/${tagId}`,
    jeTags: (auditId: string, dataSourceId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSourceId}/je-tags`,

    // CAP-025 — the import spine.
    /** POST enqueues; GET lists every run of this data source, newest first. */
    imports: (auditId: string) => `/audit/${auditId}/data-analysis/imports`,
    importsLatest: (auditId: string) => `/audit/${auditId}/data-analysis/imports/latest`,
    /**
     * The two ways out of a HALTED run (S15) — and they act on the JOB, not on a
     * version, because a gated run never composed one.
     *
     * Reprocess returns the SAME job id to `pending`: a halt and its resumption are
     * one run with an evaluation history, not two runs. The cancel is the gated
     * run's own terminal exit and is NOT `versionCancel` — that one ends a composed
     * pending version and leaves the job `succeeded`. Routing them by state is the
     * whole point (delta §3.5); a `pending`/`running` job has no cancel at all.
     */
    /**
     * Open or resume the SHARED Upload run (contract 0.50.0).
     *
     * Materialises the run BEFORE its files, so the wizard has a record to hang
     * staged mapping, the step pointer and "who last worked on it" from. A repeat
     * call resumes the incumbent run at whatever status it actually reached — this
     * is a resume verb, not a create verb — and `POST /imports` then promotes that
     * exact draft, which is why the enqueue now requires its id and generation.
     */
    importDrafts: (auditId: string) => `/audit/${auditId}/data-analysis/imports/drafts`,
    /**
     * Move the SHARED run's step pointer (§4.1 / §6.2.24) — the only route that writes
     * it after promotion parks it at `extraction`.
     *
     * NAVIGATION, NEVER A GATE (I-1): no gated route reads the column, so a refusal
     * here is a fact about the stepper and never about what the auditor may do next.
     * Its vocabulary is the API's own six words, not the wizard's seven step ids —
     * `lib/wizardPointer.ts` owns the crossing.
     */
    importWizardStep: (auditId: string, jobId: string | number) =>
      `/audit/${auditId}/data-analysis/imports/${jobId}/wizard-step`,
    importReprocess: (auditId: string, jobId: string | number) =>
      `/audit/${auditId}/data-analysis/imports/${jobId}/reprocess`,
    /** Re-anchor a composed run to a different engagement period — the KIND alone;
     *  explicit dates only buy a 409 class. */
    importCoverageRecompose: (auditId: string, jobId: string | number) =>
      `/audit/${auditId}/data-analysis/imports/${jobId}/coverage-recompose`,
    importCancel: (auditId: string, jobId: string | number) =>
      `/audit/${auditId}/data-analysis/imports/${jobId}/cancel`,
    /** The period the run works on (#442, wizard §6.2.62): `GET` reads it, `PUT` holds an
     *  edit with the run until *Confirm import*. */
    importPeriod: (auditId: string, jobId: string | number) =>
      `/audit/${auditId}/data-analysis/imports/${jobId}/period`,
    /** The run's OWN files (#476): what Upload lists, with or without a version. */
    importDelivery: (auditId: string, jobId: string | number) =>
      `/audit/${auditId}/data-analysis/imports/${jobId}/delivery`,
    /** A gated evaluation's evidence (#1020): seated engagement auditors only, 404 to anyone else. */
    importGateEvidence: (auditId: string, jobId: string | number) =>
      `/audit/${auditId}/data-analysis/imports/${jobId}/gate-evidence`,
    dataSources: (auditId: string) => `/audit/${auditId}/data-analysis/data-sources`,
    dataSourceChanges: (auditId: string, dataSourceId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSourceId}/changes`,

    // CAP-024 — the import wizard's lifecycle spine. Every composition lands in
    // `pending` and leaves it through exactly one of confirm or cancel; the record
    // is the standing evidence for what the run read (wizard §4.6).
    /** VERSION-addressed, not job-addressed: bexio mints no job, a failed job records no version. */
    versionRecord: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/record`,
    /**
     * The auditor's own prose on a PENDING version — one PUT per `(grain, key)` slot,
     * where a body of `''` clears it. Read back on `versionRecord.documentation`; no
     * GET of its own, no DELETE (being a non-GET is what makes the frozen-audit 404 fire).
     */
    versionDocumentation: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/documentation`,
    /**
     * One wizard table's column widths on a PENDING version (wizard §6.2.71, #842) — a
     * whole-blob replace; read back on `versionRecord.tableLayouts`. `tableKey` is
     * `view:<validationKey>`, `drill:<viewId>` or `review`, so it is escaped.
     */
    versionTableLayout: (auditId: string, versionId: string | number, tableKey: string) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/table-layouts/${encodeURIComponent(tableKey)}`,
    /** Activates the reviewed composition. Armed — it refuses with a structured 409. */
    versionConfirm: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/confirm`,
    /**
     * The Date-coverage picture — per-day bars, lanes, bands, and the CHECKSUM that
     * both exits from `pending` now require as `expectedVersionChecksum`. Reading it
     * is what makes an activation legal: it proves the auditor confirmed the
     * composition they were actually shown.
     */
    versionCoverage: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/coverage`,
    /**
     * The import record's Map-accounts step (#558): a confirmed, superseded or cancelled
     * version's mapped statement as the decision left it, in `mappingStatement`'s shape.
     * A GET, so it reads on a frozen or archived audit too.
     */
    versionMappingStatement: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/mapping-statement`,
    /** The second exit from `pending`; without it a refused confirm strands the data source. */
    versionCancel: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/cancel`,
    /**
     * The extraction-correction exit for ONE pending opening (0.54.0, handoff §6):
     * reverses its interpreted side. `analysisLineId` is `TableRow.id`, the AnalysisLine
     * PK every regular row now carries. Pending-only; a Validate-step mutation.
     */
    versionOpeningFlipSide: (
      auditId: string,
      versionId: string | number,
      analysisLineId: string | number,
    ) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/openings/${analysisLineId}/flip-side`,
    /** Creates a draft correction journal for a version. */
    versionCorrectionJournals: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/correction-journals`,
    findings: (auditId: string) => `/audit/${auditId}/data-analysis/findings`,
    /** One Finding — read (with journals + backlinks), head patch, delete (0.54.0 §1). */
    finding: (auditId: string, findingId: string | number) =>
      `/audit/${auditId}/data-analysis/findings/${findingId}`,
    correctionJournal: (auditId: string, correctionJournalId: string | number) =>
      `/audit/${auditId}/data-analysis/correction-journals/${correctionJournalId}`,
    /**
     * THE JOURNAL'S OWN LOG — §4.3's "the history is the record, not the state" (P-18):
     * every status move and every committed field edit, with actor, timestamp, old →
     * new and the withdrawal reason, and it "shows on the tag's hover".
     *
     * Read-only and deliberately open where the writes are not: it answers on a FROZEN
     * audit and for a cancelled run's journal, because a frozen record is exactly what
     * a history is for. Paged like the findings list — `truncated` with an `afterId`
     * keyset cursor — so a long-lived journal is never a wall.
     */
    correctionJournalHistory: (auditId: string, correctionJournalId: string | number) =>
      `/audit/${auditId}/data-analysis/correction-journals/${correctionJournalId}/history`,
    /** The journal's status transition (0.54.0 §1: takes `expectedRevision`). */
    correctionJournalStatus: (auditId: string, correctionJournalId: string | number) =>
      `/audit/${auditId}/data-analysis/correction-journals/${correctionJournalId}/status`,
    /** One period's correction date (#1153): record or clear it; `periodKind` is the period's key. */
    correctionJournalDate: (
      auditId: string,
      correctionJournalId: string | number,
      periodKind: string,
    ) =>
      `/audit/${auditId}/data-analysis/correction-journals/${correctionJournalId}/correction-dates/${periodKind}`,
    /** Undo a date act by its event, or a deferred ruling by its handle (#1153). */
    correctionJournalDateUndo: (auditId: string, correctionJournalId: string | number) =>
      `/audit/${auditId}/data-analysis/correction-journals/${correctionJournalId}/correction-dates/undo`,
    /**
     * §4.4's retirement ruling — keep the auditor's correction, or mark it corrected
     * by the client's own booking. Addressed by PROPOSAL, not by journal: one journal
     * can be proposed against several candidate entries, and each is ruled on its own.
     */
    correctionRetirementDecision: (auditId: string, proposalId: string | number) =>
      `/audit/${auditId}/data-analysis/correction-retirement-proposals/${proposalId}/decision`,
    /**
     * The Review step's diff — what the confirm would activate, classified per
     * journal entry. A GET, so it also serves an already-confirmed version: §4.6
     * re-opens a completed run read-only, and serving the review and the record
     * from one route is what keeps "what was reviewed" and "what was recorded"
     * provably the same thing.
     */
    versionDiff: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/diff`,
    /**
     * §4.4's seven checks. The POST evaluates and FREEZES onto a pending version;
     * the GET serves what was frozen, or an explicit not-validated — never an
     * absence a reader could take for a pass.
     */
    versionValidate: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/validate`,
    versionValidation: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/validation`,
    /**
     * FEAT-14's platform-owned validation views — the Validate step's tile bar and
     * the selected tile's table.
     *
     * ⚠ NEVER CAPABILITY-PROBE THESE. Their 404 is byte-identical for "the route is
     * missing" (a pre-S17 API) and "that version is not in this audit", so a probe
     * would report a wrong version id as a missing feature. Call them only once the
     * already-parsed record says `validation.views.seeded === true`.
     */
    validationViews: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/validation/views`,
    validationView: (auditId: string, versionId: string | number, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/validation/views/${viewId}`,
    /** Spawn a drill tile from a fixed validation view's cell (POST), and put one away (DELETE). */
    validationViewDrill: (auditId: string, versionId: string | number, viewId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/validation/views/${viewId}/drill`,
    validationViewDrillTile: (
      auditId: string,
      versionId: string | number,
      viewId: string | number,
      drillViewId: string | number,
    ) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/validation/views/${viewId}/drill/${drillViewId}`,
    /** The data set's version history. Under `/data-sources/` because a history is a
     *  property of the DATASET, and because that id is what audit-scopes the read. */
    dataSourceVersions: (auditId: string, dataSetId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSetId}/versions`,
    /** What restoring this version would cost (identity §4.5) — a GET, so it reads on
     *  a frozen audit too; the dialog disables on its `restorable: false`. */
    versionRestorePreview: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/restore-preview`,
    /** Restores a superseded version by minting the next one (202 + operation). Exempt
     *  from the import permission (FEAT-15 §4.5 governs it), blocked on an archived audit. */
    versionRestore: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/restore`,
    /** Reopens a cancelled (or platform-failed) version's run as the next pending
     *  version (202 + operation). Behind *Can import data*. */
    versionReopen: (auditId: string, versionId: string | number) =>
      `/audit/${auditId}/data-analysis/versions/${versionId}/reopen`,
    /** What restoring v0 — the global reset — would cost. v0 has no row, so it is
     *  addressed through the data set, which audit-scopes the read. */
    dataSourceRestoreEmptyStartPreview: (auditId: string, dataSetId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSetId}/restore-v0-preview`,
    /** Restores v0 by minting the next version EMPTY (202 + operation). Exempt from the
     *  import permission like the restore, blocked on an archived audit. */
    dataSourceRestoreEmptyStart: (auditId: string, dataSetId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSetId}/restore-v0`,
    /** A restore's or a reopen's progress and outcome — open to every audit member. */
    restoreOperation: (auditId: string, operationId: string | number) =>
      `/audit/${auditId}/data-analysis/restore-operations/${operationId}`,
    /** The files already in the data set, with row-level Imported/Superseded
     *  provenance — the base a new import composes against (§6.2.15). */
    dataSourceFiles: (auditId: string, dataSetId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSetId}/files`,
    /** Which library documents the data set already holds: Upload's Imported / Superseded
     *  badge (#645, wizard §6.2.15). At most 200 ids per request. */
    dataSourceDocumentProvenance: (
      auditId: string,
      dataSetId: string | number,
      documentIds: readonly number[],
    ) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSetId}/document-provenance?documentIds=${documentIds.join(',')}`,
    /** Which source-delivered properties the data set holds no value for (CAP-025 §7.1.10). */
    dataSourcePropertyAvailability: (auditId: string, dataSetId: string | number) =>
      `/audit/${auditId}/data-analysis/data-sources/${dataSetId}/property-availability`,
    /** CAP-027's import seam: the Map-accounts step's gate preview (pending only)
     *  and the attestation that clears `mappingNotConfirmed` at activation. */
    mappingCoverage: (auditId: string) => `/audit/${auditId}/data-analysis/mapping/coverage`,
    mappingConfirm: (auditId: string) => `/audit/${auditId}/data-analysis/mapping/confirm`,
    /**
     * The run's STAGED Map-accounts judgement — one account at a time, persisted
     * immediately, and never durable mapping: `mappingConfirm` is still the single
     * acceptance moment, and it folds what was staged.
     */
    mappingStaged: (auditId: string) => `/audit/${auditId}/data-analysis/mapping/staged`,
    /**
     * The Map-accounts step's OWN statement over a version the caller pins — two row
     * levels, one amount column, the mapping fold and the coverage fence. A computed
     * read: never a saved view, never a validation view, and it writes nothing.
     *
     * Takes `datasetVersionId` (required) and the optional `assignments` payload of
     * unsaved edits. It replaced the seeded `check4` validation view the step used to
     * borrow, which filtered `entry_type: opening_balance` and grouped four deep.
     */
    mappingStatement: (auditId: string) => `/audit/${auditId}/data-analysis/mapping/statement`,
  },
  status: {
    getAll: (auditId: string) => `/audit/${auditId}/status`,
    getTasks: (auditId: string) => `/audit/${auditId}/status/tasks`,
    previewPreset: (auditId: string) => `/audit/${auditId}/status/preset/preview`,
    startEdit: (auditId: string) => `/audit/${auditId}/status/start-edit`,
    save: (auditId: string) => `/audit/${auditId}/status/save`,
    reorderSteps: (auditId: string) => `/audit/${auditId}/status/steps/reorder`,
    updatePreset: (auditId: string) => `/audit/${auditId}/status/steps/update-preset`,
    syncUpdate: (auditId: string) => `/audit/${auditId}/status/steps/sync-update`,
    /** The places that cite this task (0.54.0). Auditor role only, unfiltered. */
    stepBacklinks: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}/backlinks`,
    pullPreset: (auditId: string) => `/audit/${auditId}/status/steps/pull-preset`,
    disconnect: (auditId: string) => `/audit/${auditId}/status/steps/disconnect`,
    createStep: (auditId: string) => `/audit/${auditId}/status/steps/create`,
    updateStep: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}/update`,
    attachEntity: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}/attach-entity`,
    deleteStep: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}`,
    markAsDone: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}/mark-as-done`,
    setStatus: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}/set-status`,
    toggleStatus: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}/toggle-status`,
    updateSubtaskCategory: (auditId: string, stepId: string | number) =>
      `/audit/${auditId}/status/steps/${stepId}/category`,
    newPreset: (auditId: string) => `/audit/${auditId}/status/new-preset`,
    setPreset: (auditId: string) => `/audit/${auditId}/status/set-preset`,
    renamePreset: (auditId: string, presetId: string | number) =>
      `/audit/${auditId}/status/preset/${presetId}`,
    deletePreset: (auditId: string, presetId: string | number) =>
      `/audit/${auditId}/status/preset/${presetId}`,
  },
  publicDocument: {
    requestAccess: (documentId: string, accessCode: string) =>
      `/public/document/${documentId}/${accessCode}/request`,
    verifyAccess: (documentId: string, accessCode: string) =>
      `/public/document/${documentId}/${accessCode}/verify`,
    get: (documentId: string, accessCode: string) => `/public/document/${documentId}/${accessCode}`,
    attemptAccess: (documentId: string, accessCode: string) =>
      `/public/document/${documentId}/${accessCode}/attempt`,
    comment: (documentId: string, accessCode: string) =>
      `/public/document/${documentId}/${accessCode}/comment`,
  },
} as const;

export type EndpointValue = string | ((...args: unknown[]) => string);
